import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  IAllLeadsResponse,
  ILead,
  IPaymentsLeadsResponse,
} from "../models/Lead";
import { IPayrunPayment } from "../models/Payment";
import { DefaultResponseWithData } from "./../models/system";

export default function usePayments(includeSent = false, startDate?: string | null | undefined, endDate?: string | null | undefined, disableAllPayruns = false) {
  const { get, post, del, put } = useApiHelper();
  const queryClient = useQueryClient();

  // Depracated - replaced by PagedPaymentTable's own queries
  // const raisedPayments = useQuery({
  //   queryKey: ["raisedPayments", includeSent, startDate, endDate],
  //   queryFn: () => get<IPaymentsLeadsResponse>(`/payment` + (includeSent ? `?includeSent=true&startDate=${startDate}&endDate=${endDate}` : "")),
  //   staleTime: 1000 * 60, // 1 minute
  //   refetchInterval: 1000 * 60, // 1 minute
  //   enabled: ((includeSent && startDate && endDate) || (!includeSent && !startDate && !endDate)) ? true : false, //all or nothin'
  // });

  const allPayruns = useQuery({
    queryKey: ["raisedPayments", "payruns"],
    queryFn: () => get<IPaymentsLeadsResponse>("/payment/payruns"),
    staleTime: 1000 * 60, // 1 minute
    refetchInterval: 1000 * 60, // 1 minute
    enabled: !disableAllPayruns,
  });

  //Admin only
  const approvePayments = useMutation(
    (body: any) => post(`/payment/approve`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["raisedPayments"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["pagedTable", "/payment/filtered"]);
      },
    }
  );

  //Admin only
  const cancelPayments = useMutation((body: any) => del(`/payment`, body), {
    onSuccess: () => {
      queryClient.invalidateQueries(["raisedPayments"]);
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["pagedTable", "/payment/filtered"]);
    },
  });

  const sendPayment = useMutation(
    (leadId: string) => post(`/payment/${leadId}`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["lead"] });
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["raisedPayments"]);
        queryClient.invalidateQueries(["pagedTable", "/payment/filtered"]);
      },
    }
  );

  const bulkSendPayments = useMutation(
    (body: any) => post(`/payment/bulk/send`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["lead"] });
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["raisedPayments"]);
        queryClient.invalidateQueries(["pagedTable", "/payment/filtered"]);
      },
    }
  );

  const syncPayment = useMutation(
    (leadId: string) => post(`/payment/${leadId}/sync`, null),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["raisedPayments"]);
        queryClient.invalidateQueries(["pagedTable", "/payment/filtered"]);
      },
    }
  );

  // put request
  const setPaymentStatus = useMutation(
    (body: any) => put(`/payment/${body.leadId}/${body.transactionId}`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["raisedPayments"]);
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["pagedTable", "/payment/filtered"]);
      },
    }
  );

  const paymentStatus = useMutation(
    ({ leadId, transactionId }: { leadId: string; transactionId: string }) =>
      get<DefaultResponseWithData<IPayrunPayment>>(
        `/payment/${leadId}/${transactionId}`
      )
  );

  return {
    // raisedPayments,
    allPayruns,
    approvePayments,
    cancelPayments,
    sendPayment,
    syncPayment,
    setPaymentStatus,
    paymentStatus,
    bulkSendPayments
  };
}
